import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { BreadcrumbService, Note, UserLite } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { SupplyWarehouseDocumentType } from '../../../models/supply-warehouse/supply-warehouse-document-type.enum';
import { TransactionStrategyFactory } from '../../../services/strategies/transaction-strategy.factory';
import { BaseTransactionStrategy } from '../../../services/strategies/base-transaction.strategy';
import { GoodsReceiptEntry, GoodsReceiptStatus } from '../../../models/inventory/goods-receipt';
import { DispatchRequest, StatusDispatchRequest } from '../../../models/dispatch-request/dispatch-request';
import { MaterialIssue, StatusMaterialIssue } from '../../../models/inventory/material-issue';
import { InternalReturn, InternalReturnStatus } from '../../../models/inventory/internal-return';
import { ProviderReturn, ProviderReturnStatus } from '../../../models/inventory/provider-return';
import { Currency, PurchaseOrder, PurchaseOrderStatus } from '../../../models/order/purchase-order/purchase-order';
import { ExternalRequisition } from '../../../models/order/external-requisition/external-requisition';
import { TransactionAction } from '../../../models/supply-warehouse/transaction-action.model';
import { TableColumn } from '../../../models/supply-warehouse/table-column.model';
import { LocalHelperService } from '../../../services/local-helper.service';
import { EnumService } from '../../../services/enum.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { IncomingReceiptLineBase, OutgoingReceiptLineBase } from '../../../models/receipt-line-base';
import { StorageLocation } from '../../../models/storage-location';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';
import { LocationNode } from '../../../models/location';
import { TreeNode } from 'primeng/api';
import { ProviderReturnProxyService } from '../../../proxies/inventory/provider-return.proxy.service';

// Union types for documents and statuses
type TransactionDocument = GoodsReceiptEntry | DispatchRequest | MaterialIssue | InternalReturn | ProviderReturn;
type TransactionStatus = GoodsReceiptStatus | StatusDispatchRequest | StatusMaterialIssue | InternalReturnStatus | ProviderReturnStatus;
type InitiatorDocument = PurchaseOrder | ExternalRequisition | DispatchRequest | GoodsReceiptEntry;
type TransactionStrategy = BaseTransactionStrategy<TransactionDocument, TransactionStatus>;

type LineWithLocations = (IncomingReceiptLineBase | OutgoingReceiptLineBase) & {
  storageLocations?: StorageLocation[];
  sourceLocations?: StorageLocation[];
  locationTreeNodes?: TreeNode[]; // Per-line location tree nodes
  isLoadingLocations?: boolean; // Loading state for locations
};

// Extended StorageLocation to include TreeNode for TreeSelect
type StorageLocationWithNode = StorageLocation & {
  selectedTreeNode?: TreeNode; // TreeNode for binding to p-treeSelect
};

@Component({
  selector: 'app-supply-warehouse-detail',
  templateUrl: './supply-warehouse-detail.component.html',
  styleUrl: './supply-warehouse-detail.component.scss',
})
export class SupplyWarehouseDetailComponent implements OnInit {
  // ==================== Properties ====================
  private readonly currencyLabels: Record<number, string> = { [Currency.USD]: 'USD', [Currency.HNL]: 'HNL', [Currency.EUR]: 'EUR' };
  documentType: SupplyWarehouseDocumentType | null = null;
  initiatorId: string | null = null;
  isLoading = 0;

  // Strategy
  strategy!: TransactionStrategy;

  // Data
  initiatorDocument: InitiatorDocument | null = null;
  transactionDocuments: TransactionDocument[] = [];
  currentDocument: TransactionDocument | null = null;
  selectedDocumentId: string | null = null;

  private originalDocument: TransactionDocument | null = null;
  hasUnsavedChanges = false;

  private prAvailableQuantities = new Map<string, number>();
  private netProcessedQuantities: Map<string, number> | null = null;

  // UI State
  activeTabIndex = '0';
  expandedRowKeys: Record<string, boolean> = {};
  editMode: 'view' | 'create' | 'edit' = 'view';

  // Enums y columnas
  uoms: Record<number, string> = {};
  statusEnum: Record<number, string> = {};
  initiatorStatusEnum: Record<number, string> = {};
  tableColumns: TableColumn[] = [];
  initiatorItemsColumns: TableColumn[] = [];
  availableActions: TransactionAction[] = [];

  // Enums disponibles en el template
  readonly DocumentType = SupplyWarehouseDocumentType;
  readonly GoodsReceiptStatus = GoodsReceiptStatus;
  readonly isAdmin = AuthService.hasRole(UserGroup.SupplyWarehouseAdmin);
  readonly isInternalReturnsAdmin = AuthService.hasRole(UserGroup.SupplyWarehouseInternalReturnsAdmin);
  readonly isProviderReturnAdmin = AuthService.hasRole(UserGroup.SupplyWarehouseProviderReturnAdmin);

  // User info for notes
  currentUserLite: UserLite | null = null;

  // Location (reads from localStorage, set by list component)
  readonly LOCATION_STORAGE_KEY = 'supply-warehouse-selected-location';

  get selectedLocation(): string {
    return localStorage.getItem(this.LOCATION_STORAGE_KEY) || '';
  }

  get selectedLocationLabel(): string {
    return this.selectedLocation;
  }

  readonly selectedLocationIcon = 'pi pi-box';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private strategyFactory: TransactionStrategyFactory,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public localHelperService: LocalHelperService,
    public enumService: EnumService,
    private locationProxyService: LocationProxyService,
    private userAuthenticationService: UserAuthenticationService,
    private translateService: TranslateService,
    private providerReturnProxyService: ProviderReturnProxyService,
    private breadcrumbService: BreadcrumbService,
  ) {
    this.currentUserLite = this.userAuthenticationService.userLite;
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.documentType = params['documentType'] as SupplyWarehouseDocumentType;
      this.initiatorId = params['id'];

      if (this.documentType && this.initiatorId) {
        this.initializeStrategy();
        this.loadData();
      } else {
        this.navigateBack();
      }
    });
  }

  /**
   * Loads location trees for all lines that have existing locations
   */
  private loadLocationTreesForAllLines(): void {
    const entries = this.getDocumentLines();
    if (!this.currentDocument || entries.length === 0) return;
    if (!('locationCode' in this.currentDocument) || !this.currentDocument.locationCode) return;

    // Load location tree for each line that has locations assigned
    entries.forEach((line: LineWithLocations) => {
      const locationType = this.strategy.locationAssignmentType === 'storage' ? 'storageLocations' : 'sourceLocations';

      // Only load if line has locations but no tree loaded yet
      if (line[locationType] && line[locationType]!.length > 0 && !line.locationTreeNodes && this.currentDocument) {
        this.loadLocationTreeForLine(line, this.currentDocument.locationCode as string);
      }
    });
  }

  // ==================== Initialization ====================

  private initializeStrategy(): void {
    try {
      this.strategy = this.strategyFactory.getStrategy(this.documentType!);
      this.tableColumns = this.strategy.getTableColumns();
      this.initiatorItemsColumns = this.strategy.getInitiatorItemsColumns();
      this.loadEnums();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.navigateBack();
    }
  }

  private loadEnums(): void {
    this.uoms = this.enumService.getEnum('UnitOfMeasure', 'item').values;
    if (this.documentType === SupplyWarehouseDocumentType.GoodsReceiptNote) {
      this.statusEnum = this.enumService.getEnum('GoodsReceiptStatus', 'inventory').values;
      this.initiatorStatusEnum = this.enumService.getEnum('PurchaseOrderStatus', 'order').values;
    } else if (this.documentType === SupplyWarehouseDocumentType.DispatchRequest) {
      this.statusEnum = this.enumService.getEnum('StatusDispatchRequest', 'inventory').values;
      this.initiatorStatusEnum = this.statusEnum; // DR es su propio iniciador
    } else if (this.documentType === SupplyWarehouseDocumentType.MaterialIssue) {
      this.statusEnum = this.enumService.getEnum('StatusMaterialIssue', 'inventory').values;
      this.initiatorStatusEnum = this.enumService.getEnum('RequisitionStatus', 'inventory').values;
    } else if (this.documentType === SupplyWarehouseDocumentType.InternalReturn) {
      this.statusEnum = this.enumService.getEnum('InternalReturnStatus', 'inventory').values;
      this.initiatorStatusEnum = this.enumService.getEnum('StatusDispatchRequest', 'inventory').values;
    } else if (this.documentType === SupplyWarehouseDocumentType.ProviderReturn) {
      this.statusEnum = this.enumService.getEnum('ProviderReturnStatus', 'inventory').values;
      this.initiatorStatusEnum = this.enumService.getEnum('GoodsReceiptStatus', 'inventory').values;
    }
  }

  // ==================== Data Loading ====================

  private loadData(): void {
    this.startLoading();

    // Para Dispatch Request, cargar el documento directamente
    if (this.documentType === SupplyWarehouseDocumentType.DispatchRequest) {
      this.strategy
        .loadDocument(this.initiatorId!)
        .pipe(finalize(() => this.stopLoading()))
        .subscribe({
          next: (document) => {
            this.currentDocument = document;
            this.breadcrumbService.setCurrent(document?.number);
            // El iniciador es el mismo documento para DispatchRequest
            if (document && this.isDispatchRequest(document)) {
              this.initiatorDocument = document;
            }

            this.storeOriginalDocument();
            this.loadLocationTreesForAllLines();
            this.updateAvailableActions();
            this.activeTabIndex = '1';
            this.editMode = this.canEdit() ? 'edit' : 'view';
          },
          error: (error) => {
            console.error('Error loading document:', error);
          },
        });
      return;
    }

    // Para otros documentos (GRN, MI, etc.), cargar el iniciador primero
    this.strategy
      .loadInitiatorDocument(this.initiatorId!)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (initiator) => {
          this.initiatorDocument = initiator;
          if (this.isPurchaseOrder(initiator)) {
            this.currency = initiator.currency ?? Currency.USD;
          }
          this.loadTransactionDocuments();
        },
        error: (error) => {
          console.error('Error loading initiator:', error);
        },
      });
  }

  private loadNetProcessedQuantities(): void {
    this.strategy.loadNetProcessedQuantities(this.initiatorId!).subscribe({
      next: (quantities) => {
        this.netProcessedQuantities =
          quantities === null ? null : new Map(quantities.map((q) => [this.buildItemKey(q.itemCode, q.ownerReference), q.quantity]));
      },
      error: (error) => {
        console.error('Error loading net processed quantities:', error);
      },
    });
  }

  private buildItemKey(itemCode: string, ownerReference?: string): string {
    return `${itemCode}|${ownerReference ?? ''}`;
  }

  private loadTransactionDocuments(): void {
    this.startLoading();
    this.loadNetProcessedQuantities();

    this.strategy
      .searchTransactionDocuments(this.initiatorId!)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (documents) => {
          this.transactionDocuments = documents;

          // Si hay un documento pendiente, seleccionarlo automáticamente
          // 0 = Pending para la mayoría de documentos
          const pendingDoc = documents.find((doc) => doc.status === 0);

          if (pendingDoc) {
            this.selectDocument(pendingDoc.id!);
          } else if (
            (this.documentType === SupplyWarehouseDocumentType.InternalReturn ||
              this.documentType === SupplyWarehouseDocumentType.ProviderReturn) &&
            documents.length > 0
          ) {
            // IR and PR start at Processing (1), auto-select the first Processing one
            const processingDoc = documents.find((doc) => doc.status === 1) || documents[0];
            this.selectDocument(processingDoc.id!);
          }
        },
        error: (error) => {
          console.error('Error loading transaction documents:', error);
        },
      });
  }

  // ==================== Document Operations ====================

  // For warehouse selection dialog
  showWarehouseDialog = false;
  warehouseOptions: { label: string; value: string }[] = [];
  selectedWarehouse: string | null = null;

  createNewDocument(): void {
    // IR and PR: locationCode is not needed, the backend derives it from the initiator
    if (
      this.documentType === SupplyWarehouseDocumentType.InternalReturn ||
      this.documentType === SupplyWarehouseDocumentType.ProviderReturn
    ) {
      // For PR: warn if there is already an active (Pending/Processing) Provider Return
      if (this.documentType === SupplyWarehouseDocumentType.ProviderReturn) {
        const activePR = (this.transactionDocuments as ProviderReturn[]).find(
          (doc) => doc.status === ProviderReturnStatus.Pending || doc.status === ProviderReturnStatus.Processing,
        );
        if (activePR) {
          this.showWarning(this.translateService.instant('ProviderReturn_Exists'));
          return;
        }
      }
      this.executeCreateDocument('');
      return;
    }

    // Check if this is a GRN from a PO with multiple destination warehouses
    if (
      this.documentType === SupplyWarehouseDocumentType.GoodsReceiptNote &&
      this.isPurchaseOrder(this.initiatorDocument) &&
      this.initiatorDocument.destinationLocationCodes &&
      this.initiatorDocument.destinationLocationCodes.length > 1
    ) {
      // Show warehouse selection dialog
      const po = this.initiatorDocument as PurchaseOrder;
      this.warehouseOptions = po.destinationLocationCodes!.map((code, index) => {
        const name = po.destinationLocationNames?.[index];
        return {
          label: name?.en || name?.es || code,
          value: code,
        };
      });
      this.selectedWarehouse = this.warehouseOptions[0]?.value || null;
      this.showWarehouseDialog = true;
      return;
    }

    // Single destination or no destination codes - proceed directly
    let locationCode = this.selectedLocation;

    if (this.isPurchaseOrder(this.initiatorDocument) && this.initiatorDocument.destinationLocationCodes?.length === 1) {
      locationCode = this.initiatorDocument.destinationLocationCodes[0];
    }

    this.executeCreateDocument(locationCode);
  }

  onWarehouseSelected(): void {
    this.showWarehouseDialog = false;
    if (this.selectedWarehouse) {
      this.executeCreateDocument(this.selectedWarehouse);
    }
  }

  private executeCreateDocument(locationCode: string): void {
    this.startLoading();
    this.strategy
      .createFromInitiator(this.initiatorId!, locationCode)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (newDoc) => {
          this.transactionDocuments.unshift(newDoc);
          this.currentDocument = newDoc;
          this.breadcrumbService.setCurrent(newDoc.number);
          this.storeOriginalDocument();
          this.selectDocument(newDoc.id!);
          this.editMode = 'edit';
          this.activeTabIndex = '1';
          this.showSuccess('Common_Document_Created', 'Document created successfully');
        },
        error: (error) => {
          console.error('Error creating document:', error);
        },
      });
  }

  selectDocument(documentId: string): void {
    this.selectedDocumentId = documentId;
    this.startLoading();

    this.strategy
      .loadDocument(documentId)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (doc) => {
          this.currentDocument = doc;
          this.breadcrumbService.setCurrent(doc?.number);
          this.storeOriginalDocument();
          // Load location trees for lines with existing locations
          this.loadLocationTreesForAllLines();
          this.updateAvailableActions();
          this.editMode = this.canEdit() ? 'edit' : 'view';
          if (this.documentType === SupplyWarehouseDocumentType.ProviderReturn && doc?.id) {
            this.loadPRAvailableQuantities(doc.id);
          }
        },
        error: (error) => {
          console.error('Error loading document:', error);
        },
      });
  }

  /**
   * Obtiene el array de líneas/entries del documento actual
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDocumentLines(): any[] {
    if (!this.currentDocument) return [];
    // DispatchRequest, InternalReturn y ProviderReturn usan 'entries'; GRN y MI usan 'lines'
    if (
      this.isDispatchRequest(this.currentDocument) ||
      this.isInternalReturn(this.currentDocument) ||
      this.isProviderReturn(this.currentDocument)
    ) {
      return this.currentDocument.entries || [];
    }
    if (this.isGoodsReceiptEntry(this.currentDocument) || this.isMaterialIssue(this.currentDocument)) {
      return this.currentDocument.lines || [];
    }
    return [];
  }

  /**
   * Calculates the actual received/issued quantity for an item from the initiator document.
   * Prefers the net quantity supplied by the API when the strategy exposes one, and otherwise
   * falls back to summing all COMPLETED transaction documents (GRNs/Material Issues).
   * When a PO has multiple lines for the same item code linked to different Owner Requisitions,
   * pass ownerReference to restrict the sum to that requisition's lines only.
   */
  getActualTransactionQuantity(itemCode: string, ownerReference?: string): number {
    // El API ya netea los Provider Returns contra los Goods Receipts; el conteo en cliente no.
    if (this.netProcessedQuantities) {
      return this.netProcessedQuantities.get(this.buildItemKey(itemCode, ownerReference)) ?? 0;
    }

    if (!this.transactionDocuments || this.transactionDocuments.length === 0) {
      return 0;
    }

    // Only sum quantities from Completed documents (status = 2 for both GRN and MI)
    const completedDocs = this.transactionDocuments.filter((doc) => doc.status === 2);

    let totalQuantity = 0;

    completedDocs.forEach((doc) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let lines: any[] = [];
      if (this.isDispatchRequest(doc) || this.isInternalReturn(doc) || this.isProviderReturn(doc)) {
        lines = doc.entries || [];
      } else if (this.isGoodsReceiptEntry(doc) || this.isMaterialIssue(doc)) {
        lines = doc.lines || [];
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      lines.forEach((line: any) => {
        if (line.itemCode === itemCode && (!ownerReference || line.owner?.reference === ownerReference)) {
          totalQuantity += line.quantity || 0;
        }
      });
    });

    return totalQuantity;
  }

  private loadPRAvailableQuantities(documentId: string): void {
    this.providerReturnProxyService.getAvailableQuantities(documentId).subscribe({
      next: (response) => {
        this.prAvailableQuantities = new Map(Object.entries(response.data ?? {}));
      },
      error: () => {
        this.prAvailableQuantities = new Map();
      },
    });
  }

  /**
   * Returns the maximum returnable quantity for a PR line capped by both the GRN received
   * quantity (minus already returned) and the current available inventory balance.
   * Returns undefined for non-PR documents.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getMaxReturnQuantityForPRLine(line: any): number | undefined {
    if (this.documentType !== SupplyWarehouseDocumentType.ProviderReturn) return undefined;
    if (!this.initiatorDocument || !this.isGoodsReceiptInitiator(this.initiatorDocument)) return undefined;
    const grnLine = this.initiatorDocument.lines?.find((l) => l.itemCode === line.itemCode);
    if (!grnLine) return undefined;
    const alreadyReturned = this.getActualTransactionQuantity(line.itemCode);
    const grnCap = grnLine.quantity - alreadyReturned;
    const availableInInventory = this.prAvailableQuantities.get(line.itemCode);
    return availableInInventory !== undefined ? Math.min(grnCap, availableInInventory) : grnCap;
  }

  /**
   * Validates that PR line quantities do not exceed GRN received quantities.
   */
  private validatePRLineQuantities(): string[] {
    if (!this.initiatorDocument || !this.isGoodsReceiptInitiator(this.initiatorDocument)) return [];
    const errors: string[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.getDocumentLines().forEach((line: any) => {
      const max = this.getMaxReturnQuantityForPRLine(line);
      if (max !== undefined && line.quantity > max + 0.001) {
        const availableQty = this.prAvailableQuantities.get(line.itemCode) ?? 0;
        errors.push(
          this.translateService.instant('PR_Error_Quantity_Exceeds_Received', {
            quantity: line.quantity.toFixed(2),
            available: availableQty.toFixed(2),
          }),
        );
      }
    });
    return errors;
  }

  /**
   * Event when user changes line quantity
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  onLineQuantityChange(line: any): void {
    this.detectChanges();
  }

  /**
   * Removes a line from the current document
   */
  removeLineFromDocument(index: number): void {
    if (!this.currentDocument) return;

    this.confirmationService.confirm({
      key: 'main',
      message: this.translateService.instant('Common_Remove_Line_Confirm') || 'Are you sure you want to remove this line?',
      acceptLabel: this.translateService.instant('Common_Yes') || 'Yes',
      rejectLabel: this.translateService.instant('Common_Cancel') || 'Cancel',
      accept: () => {
        if (
          this.isDispatchRequest(this.currentDocument) ||
          this.isInternalReturn(this.currentDocument) ||
          this.isProviderReturn(this.currentDocument)
        ) {
          this.currentDocument.entries?.splice(index, 1);
        } else if (this.isGoodsReceiptEntry(this.currentDocument) || this.isMaterialIssue(this.currentDocument)) {
          this.currentDocument.lines?.splice(index, 1);
        }
        this.detectChanges();
        this.showSuccess('Common_Line_Removed', 'Line removed successfully');
      },
    });
  }

  // ==================== Location Tree Management ====================

  /**
   * Loads the location tree for TreeSelect based on parent location code and optional itemCode
   */
  private loadLocationTreeForLine(line: LineWithLocations, parentLocationCode: string): void {
    line.isLoadingLocations = true;
    const itemCode = this.strategy.initiatorName === 'Purchase Order' ? null : line.itemCode;
    this.locationProxyService.getAllChildrenLocations(parentLocationCode, itemCode).subscribe({
      next: (response) => {
        if (response.data) {
          line.locationTreeNodes = this.convertLocationsToTreeNodes(response.data);

          this.syncLocationTreeNodes(line);
        }
        line.isLoadingLocations = false;
      },
      error: (error) => {
        console.error('Error loading location tree for item:', line.itemCode, error);
        line.isLoadingLocations = false;
      },
    });
  }

  /**
   * Converts LocationNode array to PrimeNG TreeNode format
   * Only includes storable locations (leaf nodes)
   */
  private convertLocationsToTreeNodes(locations: LocationNode[]): TreeNode[] {
    // Filter to only storable locations
    // const storableLocations = locations.filter((loc) => loc.isStorable);

    // Build tree structure
    const treeNodes: TreeNode[] = [];
    const locationMap = new Map<string, TreeNode>();

    // First pass: create all nodes
    locations.forEach((loc) => {
      const node: TreeNode = {
        key: loc.locationCode,
        label: loc.code + ' - ' + loc.name.en,
        data: loc,
        leaf: true,
        selectable: loc.isStorable,
      };
      locationMap.set(loc.locationCode, node);
    });

    // Second pass: build hierarchy
    locations.forEach((loc) => {
      const node = locationMap.get(loc.locationCode);
      if (node) {
        if (loc.parentCode) {
          const parentNode = locationMap.get(loc.parentCode);
          if (parentNode) {
            if (!parentNode.children) {
              parentNode.children = [];
              parentNode.leaf = false;
            }
            parentNode.children.push(node);
          } else {
            // Parent not in storable list, add to root
            treeNodes.push(node);
          }
        } else {
          // No parent, add to root
          treeNodes.push(node);
        }
      }
    });

    return treeNodes;
  }

  // ==================== Storage Location Management ====================

  getLocationDisplayPath(node: TreeNode): string {
    if (!node?.key) return '';
    const parts = (node.key as string).split('.');
    return parts.length > 1 ? parts.slice(1).join('.') : (node.key as string);
  }

  /**
   * Handles location selection from TreeSelect
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onLocationSelected(location: StorageLocationWithNode, selectedNode: TreeNode, line: LineWithLocations): void {
    if (selectedNode && selectedNode.key) {
      location.locationCode = selectedNode.key as string;
      location.selectedTreeNode = selectedNode;
      this.detectChanges();
    }
  }

  /**
   * Syncs existing locations with their TreeNode representation
   */
  private syncLocationTreeNodes(line: LineWithLocations): void {
    if (!line.locationTreeNodes) return;

    const locationType = this.strategy.locationAssignmentType === 'storage' ? 'storageLocations' : 'sourceLocations';
    const locations = line[locationType] as StorageLocationWithNode[] | undefined;

    if (locations) {
      locations.forEach((loc) => {
        // Find the TreeNode that matches this location's code
        const matchingNode = this.findNodeByKey(line.locationTreeNodes!, loc.locationCode);
        if (matchingNode) {
          loc.selectedTreeNode = matchingNode;
        }
      });
    }
  }

  /**
   * Recursively finds a TreeNode by its key
   */
  private findNodeByKey(nodes: TreeNode[], key: string): TreeNode | null {
    for (const node of nodes) {
      if (node.key === key) {
        return node;
      }
      if (node.children) {
        const found = this.findNodeByKey(node.children, key);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Adds a new storage location to a line item
   */
  addLocationToLine(line: LineWithLocations): void {
    // Load locations for this specific item if not already loaded
    if (!line.locationTreeNodes && this.currentDocument && 'locationCode' in this.currentDocument && this.currentDocument.locationCode) {
      this.loadLocationTreeForLine(line, this.currentDocument.locationCode);
    }

    const locationType = this.strategy.locationAssignmentType === 'storage' ? 'storageLocations' : 'sourceLocations';

    // Initialize array if it doesn't exist
    if (!line[locationType]) {
      line[locationType] = [];
    }

    // Calculate remaining quantity
    const totalAllocated = this.getTotalLocationQuantity(line[locationType]!);
    const remainingQuantity = line.quantity - totalAllocated;

    // Add new location with remaining quantity or 0
    line[locationType]!.push({
      locationCode: this.currentDocument?.locationCode || '',
      quantity: remainingQuantity > 0 ? remainingQuantity : 0,
    });

    this.detectChanges();
  }

  /**
   * Removes a storage location from a line item
   */
  removeLocationFromLine(line: LineWithLocations, locationIndex: number): void {
    const locationType = this.strategy.locationAssignmentType === 'storage' ? 'storageLocations' : 'sourceLocations';

    if (line[locationType] && line[locationType]!.length > locationIndex) {
      line[locationType]!.splice(locationIndex, 1);
      this.detectChanges();
    }
  }

  /**
   * Calculates the total quantity allocated across locations
   */
  getTotalLocationQuantity(locations: StorageLocation[]): number {
    if (!locations || locations.length === 0) return 0;
    return locations.reduce((sum, loc) => sum + (loc.quantity || 0), 0);
  }

  /**
   * Validates that location quantities match the line quantity
   */
  validateLocationQuantities(line: LineWithLocations): void {
    const locationType = this.strategy.locationAssignmentType === 'storage' ? 'storageLocations' : 'sourceLocations';

    const totalAllocated = this.getTotalLocationQuantity(line[locationType] || []);
    const diff = Math.abs(totalAllocated - line.quantity);

    // If difference exceeds tolerance, show warning
    if (diff > 0.001) {
      console.warn(`Line ${line.itemCode}: Total allocated (${totalAllocated}) does not match line quantity (${line.quantity})`);
    }
  }

  /**
   * Validates all lines to ensure location quantities match line quantities
   * Returns validation result with error messages
   */
  validateAllLocationQuantities(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const lines = this.getDocumentLines() as LineWithLocations[];

    if (!lines || lines.length === 0) {
      return { valid: true, errors: [] };
    }

    const locationType = this.strategy.locationAssignmentType === 'storage' ? 'storageLocations' : 'sourceLocations';

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    lines.forEach((line, index) => {
      const locations = line[locationType];

      if (!locations || locations.length === 0) {
        const noLocKey = locationType === 'storageLocations' ? 'IR_Error_No_Storage_Locations_Line' : 'MI_Error_No_Source_Locations_Line';
        errors.push(`${line.itemCode}: ${this.translateService.instant(noLocKey)}`);
        return;
      }

      const totalAllocated = this.getTotalLocationQuantity(locations);
      const diff = Math.abs(totalAllocated - line.quantity);

      if (diff > 0.001) {
        const mismatchKey = locationType === 'storageLocations' ? 'IR_Error_Location_Mismatch_Line' : 'MI_Error_Location_Mismatch_Line';
        errors.push(
          `${line.itemCode}: ${this.translateService.instant(mismatchKey)} (${totalAllocated.toFixed(2)} / ${line.quantity.toFixed(2)})`,
        );
      }
    });

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  private expandLinesWithoutLocations(): void {
    const lines = this.getDocumentLines() as LineWithLocations[];
    const locationType = this.strategy.locationAssignmentType === 'storage' ? 'storageLocations' : 'sourceLocations';
    lines.forEach((line) => {
      const locations = line[locationType];
      if (!locations || locations.length === 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.expandedRowKeys[(line as any).lineId] = true;
      }
    });
  }

  executeAction(action: TransactionAction): void {
    this.detectChanges();

    // For PR Complete: validate return reason before anything else
    if (action.id === 'complete' && this.documentType === SupplyWarehouseDocumentType.ProviderReturn) {
      if (this.isProviderReturn(this.currentDocument) && !this.currentDocument?.returnReason?.trim()) {
        this.showError(this.translateService.instant('PR_Error_No_Return_Reason') || 'Return reason is required');
        return;
      }
    }

    if (action.id === 'complete' || this.hasUnsavedChanges) {
      const validation = this.validateAllLocationQuantities();
      if (!validation.valid) {
        const prefix = this.translateService.instant('Common_Cannot_Save');
        this.showError(`${prefix}: ${validation.errors.join('; ')}`);
        this.expandLinesWithoutLocations();
        return;
      }
    }

    if (action.requiresConfirmation) {
      this.confirmationService.confirm({
        key: 'main',
        message: action.confirmationMessage || '¿Está seguro?',
        acceptLabel: this.translateService.instant('Common_Yes') || 'Yes',
        rejectLabel: this.translateService.instant('Common_Cancel') || 'Cancel',
        accept: () => this.performAction(action),
      });
    } else {
      this.performAction(action);
    }
  }

  private performAction(action: TransactionAction): void {
    if (this.hasUnsavedChanges && action.id !== 'save') {
      this.saveDocumentBeforeAction(action);
    } else {
      this.executeActionDirectly(action);
    }
  }

  private saveDocumentBeforeAction(action: TransactionAction): void {
    if (!this.currentDocument || !this.currentDocument.id) return;

    this.startLoading();
    this.strategy
      .updateDocument(this.currentDocument.id, this.stripUIProperties(this.currentDocument))
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (updated) => {
          this.currentDocument = updated;
          this.storeOriginalDocument(); // Update the stored version
          this.showSuccess('Common_Changes_Saved', 'Changes saved successfully');

          this.executeActionDirectly(action);
        },
        error: (error) => {
          console.error('Error saving document before action:', error);
        },
      });
  }

  private executeActionDirectly(action: TransactionAction): void {
    switch (action.id) {
      case 'start-processing':
        this.changeStatus(this.getStatusForAction('Processing'));
        break;
      case 'complete':
        this.changeStatus(this.getStatusForAction('Completed'));
        break;
      case 'back-to-pending':
        this.changeStatus(this.getStatusForAction('Pending'));
        break;
      case 'cancel':
        this.changeStatus(this.getStatusForAction('Cancelled'));
        break;
      case 'reopen':
        this.changeStatus(this.getStatusForAction('Pending'));
        break;
    }
  }

  private getStatusForAction(statusName: 'Pending' | 'Processing' | 'Completed' | 'Cancelled'): number {
    switch (this.documentType) {
      case SupplyWarehouseDocumentType.GoodsReceiptNote:
        return GoodsReceiptStatus[statusName];
      case SupplyWarehouseDocumentType.DispatchRequest:
        return StatusDispatchRequest[statusName];
      case SupplyWarehouseDocumentType.MaterialIssue:
        return StatusMaterialIssue[statusName];
      case SupplyWarehouseDocumentType.InternalReturn:
        return InternalReturnStatus[statusName];
      case SupplyWarehouseDocumentType.ProviderReturn:
        return ProviderReturnStatus[statusName];
      default:
        return 0;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private changeStatus(newStatus: any): void {
    if (!this.currentDocument || !this.currentDocument.id) return;

    // For PR: validate quantities do not exceed GRN received amounts
    if (this.documentType === SupplyWarehouseDocumentType.ProviderReturn) {
      const qtyErrors = this.validatePRLineQuantities();
      if (qtyErrors.length > 0) {
        this.showError(qtyErrors.join('; '));
        return;
      }
    }

    // Validar antes de cambiar
    const validation = this.strategy.validateForStatusChange(this.currentDocument, newStatus);
    if (!validation.valid) {
      this.showError(validation.errors.join(', '));
      return;
    }

    this.startLoading();
    this.strategy
      .updateStatus(this.currentDocument.id, newStatus)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (updated) => {
          this.currentDocument = updated;
          this.storeOriginalDocument();
          this.updateAvailableActions();
          this.showSuccess('Common_Status_Updated', 'Status updated successfully');
          this.loadTransactionDocuments(); // Refresh list
        },
        error: (error) => {
          console.error('Error updating status:', error);
        },
      });
  }

  // ==================== Change Detection ====================

  /**
   * Creates a deep copy of the document stripping UI-only properties
   * (TreeNode refs cause circular JSON errors)
   */
  private stripUIProperties(doc: TransactionDocument): TransactionDocument {
    return JSON.parse(
      JSON.stringify(doc, (key, value) => {
        if (
          key === 'selectedTreeNode' ||
          key === 'locationTreeNode' ||
          key === 'locationTreeNodes' ||
          key === 'isLoadingLocations' ||
          key === 'itemDisplayName' ||
          key === 'calculatedQuantity' ||
          key === 'isQuantityValid'
        ) {
          return undefined;
        }
        return value;
      }),
    );
  }

  /**
   * Creates a deep copy of the current document to track changes
   */
  private storeOriginalDocument(): void {
    if (this.currentDocument) {
      this.originalDocument = this.stripUIProperties(this.currentDocument);
      this.hasUnsavedChanges = false;
    }
  }

  /**
   * Checks if the current document has unsaved changes
   */
  detectChanges(): void {
    if (!this.currentDocument || !this.originalDocument) {
      this.hasUnsavedChanges = false;
      return;
    }

    const current = JSON.stringify(this.stripUIProperties(this.currentDocument));
    const original = JSON.stringify(this.originalDocument);
    this.hasUnsavedChanges = current !== original;
  }

  /**
   * Manually trigger change detection (can be called from template)
   */
  onDocumentChange(): void {
    this.detectChanges();
  }

  // ==================== Type Guards ====================

  isPurchaseOrder(doc: InitiatorDocument | null): doc is PurchaseOrder {
    return doc !== null && 'purchaseOrderNumber' in doc;
  }

  get isInitiatorPOReceived(): boolean {
    return (
      this.documentType === SupplyWarehouseDocumentType.GoodsReceiptNote &&
      this.isPurchaseOrder(this.initiatorDocument) &&
      this.initiatorDocument.status === PurchaseOrderStatus.Received
    );
  }

  getCurrencyName(currency: number | null | undefined): string {
    return currency == null ? '' : (this.currencyLabels[currency] ?? '');
  }

  isExternalRequisition(doc: InitiatorDocument | null): doc is ExternalRequisition {
    return doc !== null && 'requiredApprovers' in doc;
  }

  isDispatchRequest(doc: TransactionDocument | InitiatorDocument | null): doc is DispatchRequest {
    return doc !== null && 'dueDate' in doc && 'entries' in doc;
  }

  isInternalReturn(doc: TransactionDocument | InitiatorDocument | null): doc is InternalReturn {
    // Use receiverUserName to distinguish IR from PR (both have returnDate + entries)
    return doc !== null && 'returnDate' in doc && 'receiverUserName' in doc;
  }

  isProviderReturn(doc: TransactionDocument | InitiatorDocument | null): doc is ProviderReturn {
    return doc !== null && 'returnDate' in doc && 'returnReason' in doc;
  }

  isGoodsReceiptEntry(doc: TransactionDocument | null): doc is GoodsReceiptEntry {
    return doc !== null && 'lines' in doc && this.documentType === SupplyWarehouseDocumentType.GoodsReceiptNote;
  }

  isGoodsReceiptInitiator(doc: InitiatorDocument | null): doc is GoodsReceiptEntry {
    return doc !== null && 'lines' in doc && 'providerReference' in doc;
  }

  isMaterialIssue(doc: TransactionDocument | null): doc is MaterialIssue {
    return doc !== null && 'lines' in doc && this.documentType === SupplyWarehouseDocumentType.MaterialIssue;
  }

  providerName = '';
  currency: Currency = Currency.USD;
  /**
   * Gets the items/entries from the initiator document
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getInitiatorItems(): any[] {
    if (!this.initiatorDocument) return [];
    if (this.isPurchaseOrder(this.initiatorDocument)) {
      this.providerName = this.initiatorDocument.provider?.name || '';
      this.currency = this.initiatorDocument.currency || Currency.USD;
      return this.initiatorDocument.items || [];
    }
    if (this.isExternalRequisition(this.initiatorDocument)) {
      return this.initiatorDocument.entries || [];
    }
    if (this.isDispatchRequest(this.initiatorDocument)) {
      return this.initiatorDocument.entries || [];
    }
    if (this.isGoodsReceiptInitiator(this.initiatorDocument)) {
      return this.initiatorDocument.lines || [];
    }
    return [];
  }

  getDocumentTotalPrice(doc: TransactionDocument): number {
    if (!this.isGoodsReceiptEntry(doc)) return 0;
    return doc.lines.reduce((sum, line) => sum + line.quantity * (line.unitPrice ?? 0) * (1 + (line.taxRate ?? 0)), 0);
  }

  getDocumentUnitPrice(doc: TransactionDocument): number | null {
    if (!this.isGoodsReceiptEntry(doc) || doc.lines.length === 0) return null;
    const prices = [...new Set(doc.lines.map((l) => l.unitPrice ?? 0))];
    return prices.length === 1 ? prices[0] : null;
  }

  navigateToProviderReturn(grnId: string): void {
    this.router.navigate(['/supply-warehouse-detail'], {
      queryParams: {
        documentType: SupplyWarehouseDocumentType.ProviderReturn,
        id: grnId,
      },
    });
  }

  get allItemsFullyReturned(): boolean {
    if (this.documentType !== SupplyWarehouseDocumentType.InternalReturn) return false;
    const items = this.getInitiatorItems();
    if (items.length === 0) return false;
    return items.every((item) => item.quantity - this.getActualTransactionQuantity(item.itemCode) <= 0);
  }

  // ==================== UI Helpers ====================

  private hasWriteAccess(): boolean {
    if (this.documentType === SupplyWarehouseDocumentType.ProviderReturn) return this.isProviderReturnAdmin;
    if (this.documentType === SupplyWarehouseDocumentType.InternalReturn) return this.isAdmin || this.isInternalReturnsAdmin;
    return this.isAdmin;
  }

  private updateAvailableActions(): void {
    if (this.currentDocument && this.strategy) {
      if (!this.hasWriteAccess()) {
        this.availableActions = [];
        return;
      }
      this.availableActions = this.strategy.getAvailableActions(this.currentDocument.status as TransactionStatus, this.currentDocument);
    } else {
      this.availableActions = [];
    }
  }

  canEdit(): boolean {
    if (!this.currentDocument || !this.strategy) return false;
    if (!this.hasWriteAccess()) return false;
    return this.strategy.canEditLines(this.currentDocument.status);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onDispatchNoteAdded(note: Note): void {
    if (this.currentDocument && this.isDispatchRequest(this.currentDocument)) {
      if (!this.currentDocument.notes) {
        this.currentDocument.notes = [];
      }
      this.hasUnsavedChanges = true;
    }
  }

  getDocumentTitle(): string {
    if (!this.strategy) return 'Supply Warehouse Detail';
    return this.strategy.getDocumentTitle(this.currentDocument);
  }

  getStatusLabel(status: number): string {
    const enumName = this.statusEnum[status];
    if (!enumName) return 'Unknown';
    const key = `Common_Status_${enumName}`;
    const result = this.translateService.instant(key);
    if (result !== key) return result;
    // Fallback a clave específica del documento (ej: IR_Status_Rejected)
    const docPrefix = this.documentType ?? 'Doc';
    const specificKey = `${docPrefix}_Status_${enumName}`;
    const specific = this.translateService.instant(specificKey);
    return specific !== specificKey ? specific : enumName;
  }

  getInitiatorStatusLabel(status: number): string {
    const enumName = this.initiatorStatusEnum[status];
    if (!enumName) return 'Unknown';
    const key = `Common_Status_${enumName}`;
    const result = this.translateService.instant(key);
    return result !== key ? result : enumName;
  }

  getStatusSeverity(status: number) {
    if (this.documentType === SupplyWarehouseDocumentType.GoodsReceiptNote) {
      switch (status) {
        case GoodsReceiptStatus.Pending:
          return 'warn';
        case GoodsReceiptStatus.Processing:
          return 'info';
        case GoodsReceiptStatus.Completed:
          return 'success';
        case GoodsReceiptStatus.Cancelled:
          return 'danger';
        default:
          return 'info';
      }
    } else if (this.documentType === SupplyWarehouseDocumentType.DispatchRequest) {
      switch (status) {
        case StatusDispatchRequest.Pending:
          return 'warn';
        case StatusDispatchRequest.Processing:
          return 'info';
        case StatusDispatchRequest.Completed:
          return 'success';
        case StatusDispatchRequest.Cancelled:
          return 'danger';
        default:
          return 'info';
      }
    } else if (this.documentType === SupplyWarehouseDocumentType.MaterialIssue) {
      switch (status) {
        case StatusMaterialIssue.Pending:
          return 'warn';
        case StatusMaterialIssue.Processing:
          return 'info';
        case StatusMaterialIssue.Completed:
          return 'success';
        case StatusMaterialIssue.Cancelled:
          return 'danger';
        default:
          return 'info';
      }
    } else if (this.documentType === SupplyWarehouseDocumentType.InternalReturn) {
      switch (status) {
        case InternalReturnStatus.Pending:
          return 'warn';
        case InternalReturnStatus.Processing:
          return 'info';
        case InternalReturnStatus.Completed:
          return 'success';
        case InternalReturnStatus.Cancelled:
          return 'danger';
        default:
          return 'info';
      }
    }
    return 'info';
  }

  // ==================== Loading & Messages ====================

  private startLoading(): void {
    this.isLoading++;
  }

  private stopLoading(): void {
    this.isLoading = Math.max(0, this.isLoading - 1);
  }

  private showSuccess(messageKey: string, fallback: string): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('Common_Success') || 'Success',
      detail: this.translateService.instant(messageKey) || fallback,
      life: 3000,
    });
  }

  private showWarning(message: string): void {
    this.messageService.add({
      severity: 'warn',
      detail: message,
      life: 5000,
    });
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 5000,
    });
  }

  navigateBack(): void {
    this.router.navigate(['/supply-warehouse']);
  }

  // ==================== Report Download ====================

  downloadReport(): void {
    if (!this.currentDocument?.id || !this.strategy.supportsReportDownload()) {
      return;
    }

    this.startLoading();
    this.strategy
      .downloadReport(this.currentDocument.id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (blob) => {
          const fileName = `${this.getDocumentTitle()}.pdf`;
          this.downloadBlob(blob, fileName);
        },
        error: (error) => {
          console.error('Error downloading report:', error);
        },
      });
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
